/* eslint-disable max-len */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  lockedItemsByUser: {},
  enhancedLockDetails: {},
};

const handleAddLockedLineItems = (state, { lineItemIds, user, enhancedLockDetails }) => {
  const { userId } = user;

  if (!state.lockedItemsByUser[userId]) {
    state.lockedItemsByUser[userId] = { items: [], user };
  }

  const newItems = lineItemIds.filter(
    (itemId) => !state.lockedItemsByUser[userId].items.includes(itemId)
  );
  state.lockedItemsByUser[userId].items.push(...newItems);

  if (enhancedLockDetails && Array.isArray(enhancedLockDetails)) {
    enhancedLockDetails.forEach(({ lineItemId, orderId }) => {
      if (lineItemId && orderId) {
        state.enhancedLockDetails[lineItemId] = orderId;
      }
    });
  }
};

const handleRemoveLockedLineItems = (state, { lineItemIds, user, enhancedLockDetails }) => {
  const { userId } = user;

  if (state.lockedItemsByUser[userId]) {
    state.lockedItemsByUser[userId].items = state.lockedItemsByUser[userId].items.filter(
      (itemId) => !lineItemIds.includes(itemId)
    );

    if (state.lockedItemsByUser[userId].items.length === 0) {
      delete state.lockedItemsByUser[userId];
    }
  }

  if (enhancedLockDetails && Array.isArray(enhancedLockDetails)) {
    enhancedLockDetails.forEach(({ lineItemId }) => {
      if (lineItemId && state.enhancedLockDetails[lineItemId]) {
        delete state.enhancedLockDetails[lineItemId];
      }
    });
  } else {
    lineItemIds.forEach((lineItemId) => {
      if (state.enhancedLockDetails[lineItemId]) {
        delete state.enhancedLockDetails[lineItemId];
      }
    });
  }
};

const handleClearAllLockedItems = (state) => {
  state.lockedItemsByUser = {};
  state.enhancedLockDetails = {};
};

const handleClearUserLockedItems = (state, userId) => {
  if (state.lockedItemsByUser[userId]) {
    const userItems = state.lockedItemsByUser[userId].items || [];
    userItems.forEach((lineItemId) => {
      if (state.enhancedLockDetails[lineItemId]) {
        delete state.enhancedLockDetails[lineItemId];
      }
    });

    delete state.lockedItemsByUser[userId];
  }
};

const handleLoadAppLockedLineItems = (state, data) => {
  state.lockedItemsByUser = {};
  state.enhancedLockDetails = {};

  if (Array.isArray(data) && data.length > 0) {
    const payload = data[0];

    if (payload && typeof payload === 'object') {
      const { allLockedItems, enhancedLockDetails } = payload;

      if (Array.isArray(allLockedItems)) {
        allLockedItems.forEach((item) => {
          const { user, lineItemIds, enhancedLockDetails: itemEnhancedDetails } = item;

          if (user && user.userId && lineItemIds && Array.isArray(lineItemIds)) {
            state.lockedItemsByUser[user.userId] = {
              items: [...lineItemIds],
              user: {
                userId: user.userId,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
              },
            };

            if (itemEnhancedDetails && Array.isArray(itemEnhancedDetails)) {
              itemEnhancedDetails.forEach(({ lineItemId, orderId }) => {
                if (lineItemId && orderId) {
                  state.enhancedLockDetails[lineItemId] = orderId;
                }
              });
            }
          }
        });
      }

      if (enhancedLockDetails && Array.isArray(enhancedLockDetails)) {
        enhancedLockDetails.forEach(({ lineItemId, orderId }) => {
          if (lineItemId && orderId) {
            state.enhancedLockDetails[lineItemId] = orderId;
          }
        });
      }
    }
  }
};

const handleRemoveExpiredLineItems = (state, { lineItemIds, enhancedLockDetails } = {}) => {
  const itemsToRemove = Array.isArray(lineItemIds) ? lineItemIds : [];

  if (itemsToRemove.length > 0) {
    Object.keys(state.lockedItemsByUser).forEach((userId) => {
      if (state.lockedItemsByUser[userId]) {
        state.lockedItemsByUser[userId].items = state.lockedItemsByUser[userId].items.filter(
          (itemId) => !itemsToRemove.includes(itemId)
        );

        if (state.lockedItemsByUser[userId].items.length === 0) {
          delete state.lockedItemsByUser[userId];
        }
      }
    });
  }

  if (enhancedLockDetails && Array.isArray(enhancedLockDetails)) {
    enhancedLockDetails.forEach(({ lineItemId }) => {
      if (lineItemId && state.enhancedLockDetails[lineItemId]) {
        delete state.enhancedLockDetails[lineItemId];
      }
    });
  } else if (itemsToRemove.length > 0) {
    itemsToRemove.forEach((lineItemId) => {
      if (state.enhancedLockDetails[lineItemId]) {
        delete state.enhancedLockDetails[lineItemId];
      }
    });
  }
};

/* eslint no-param-reassign: ["error", { "ignorePropertyModificationsFor": ["state"] }] */
const lockedLineItemsSlice = createSlice({
  name: 'lockedLineItems',
  initialState,
  reducers: {
    addLockedLineItems(state, action) {
      handleAddLockedLineItems(state, action.payload);
    },
    removeLockedLineItems(state, action) {
      handleRemoveLockedLineItems(state, action.payload);
    },
    clearAllLockedItems(state) {
      handleClearAllLockedItems(state);
    },
    clearUserLockedItems(state, action) {
      handleClearUserLockedItems(state, action.payload.userId);
    },
    loadAppLockedLineItems(state, action) {
      handleLoadAppLockedLineItems(state, action.payload.data);
    },
    removeExpiredLineItems(state, action) {
      handleRemoveExpiredLineItems(state, action.payload);
    },
  },
});

export const lockedLineItemsActions = lockedLineItemsSlice.actions;
export default lockedLineItemsSlice.reducer;

// Selector functions for easy access to locked items data
export const selectCurrentUserLockedItemsById = (state, currentUserId) => {
  return state.inventory?.lockedLineItems?.lockedItemsByUser?.[currentUserId]?.items || [];
};

export const selectOtherUsersLockedItemsById = (state, currentUserId) => {
  const byUser = state.inventory?.lockedLineItems?.lockedItemsByUser || {};
  return Object.entries(byUser)
    .filter(([userId]) => userId !== String(currentUserId))
    .flatMap(([, userData]) => userData.items);
};

export const selectLockedItemsUserMapById = (state, currentUserId) => {
  const byUser = state.inventory?.lockedLineItems?.lockedItemsByUser || {};
  const map = {};
  Object.entries(byUser).forEach(([userId, userData]) => {
    if (userId !== String(currentUserId)) {
      userData.items.forEach((lineItemId) => {
        map[lineItemId] = userData.user;
      });
    }
  });
  return map;
};

export const selectLockedOrdersUserMap = (state, currentUserId) => {
  const byUser = state.inventory?.lockedLineItems?.lockedItemsByUser || {};
  const enhancedDetails = state.inventory?.lockedLineItems?.enhancedLockDetails || {};
  const map = {};

  Object.entries(byUser).forEach(([userId, userData]) => {
    if (userId !== String(currentUserId)) {
      const items = userData.items || [];
      items.forEach((lineItemId) => {
        const orderId = enhancedDetails[lineItemId];
        if (orderId && !map[orderId]) {
          map[orderId] = userData.user;
        }
      });
    }
  });

  return map;
};
